import * as vp from 'vopay';
import * as Yup from 'yup';
const { TextField, Select } = vp;
import { FieldData } from '../../../../Form/types';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

type AccountTranferRowsProp = {
  MainAccountName: string;
  MainAccountId: string;
  MainAccountBalance: string;
  fromSubAccounts: vp.PortalSubAccount[];
  toSubAccounts: vp.PortalSubAccount[];
  fromFetching: boolean;
  toFetching: boolean;
  country: string;
  initialFromLabel: string;
  onSearchFromSubAccounts: (searchText: string) => void;
  onSearchToSubAccounts: (searchText: string) => void;
  fetchFromSubAccountsAsync: (params: vp.SubAccountsRequest) => Promise<vp.SubAccountsResponse>;
  fetchToSubAccountsAsync: (params: vp.SubAccountsRequest) => Promise<vp.SubAccountsResponse>;
};

export type AccountTransferRequest = {
  Amount: string;
  RecipientAccountID: string;
  DebitorAccountID: string;
  Currency: string;
  // Available balance of the picked "From" account. Kept in the form (not a
  // separate ref/state) so resolving it changes ctx, which rebuilds the schema
  // and re-validates the amount. Not a rendered field and not serialized.
  FromBalance?: string;
};
export type AccountTransferSerializedRequest = {
  Amount: number;
  RecipientAccountID: string;
  DebitorAccountID: string;
  Currency: string;
};

export const useAccountTransferRows = ({
  MainAccountName,
  MainAccountBalance,
  MainAccountId,
  fromSubAccounts,
  toSubAccounts,
  fromFetching,
  toFetching,
  country,
  initialFromLabel,
  onSearchFromSubAccounts,
  onSearchToSubAccounts,
  fetchFromSubAccountsAsync,
  fetchToSubAccountsAsync,
}: AccountTranferRowsProp): FieldData<AccountTransferRequest>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart.AccountTransfer' });
  const [fromBankLabel, setFromBankLabel] = useState(initialFromLabel);
  const [toBankLabel, setToBankLabel] = useState('');
  const [fromSearchText, setFromSearchText] = useState('');
  const [toSearchText, setToSearchText] = useState('');

  const CurrencyOptions = {
    ...vp.CURRENCY_LIST_OPTIONS,
  };

  return [
    {
      label: () => t('Currency.label'),
      fieldName: 'Currency',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            options={country === 'US' ? vp.convertToOptions(vp.US_CURRENCY_ONLY) : vp.convertToOptions(CurrencyOptions)}
            placeholder={t('Currency.placeholder')}
            {...fieldData}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('From.label'),
      fieldName: 'DebitorAccountID',
      getInputState: () => 'required',
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const FromBankAccountsOptions = buildSubAccountOptions(
          fromSubAccounts.filter((data) => ctx.values.RecipientAccountID !== data.APIAccountID),
          false,
          fromSearchText,
        );
        return (
          <Select
            version="2"
            options={FromBankAccountsOptions}
            placeholder={t('From.placeholder')}
            sort={false}
            filterable
            loading={fromFetching}
            emptyFilterMsg={t('From.noResults', { defaultValue: 'No sub-accounts found' })}
            onFilter={(e) => {
              e.preventDefault();
              setFromSearchText(e.filter);
              if (e.filter.length > 0 && e.filter.length < SEARCH_MIN_CHARS) return;
              e.debounce(() => onSearchFromSubAccounts(e.filter), SEARCH_DEBOUNCE_MS);
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;
              return <>{fromBankLabel || value}</>;
            }}
            {...fieldData}
            onChange={async (e, { value, label }) => {
              fieldData.onChange(e);

              const applyBalance = (balance?: string) => {
                ctx.setFieldValue('FromBalance', balance, false);
              };
              if (!value) {
                setFromBankLabel('');
                applyBalance(undefined);
                return;
              }
              if (value === MainAccountId) {
                setFromBankLabel(`${MainAccountName} ($${vp.formattedCurrency(MainAccountBalance || '0.00')})`);
                applyBalance(MainAccountBalance || '0.00');
                return;
              }
              try {
                const res = await fetchFromSubAccountsAsync({ ActiveOnly: true, Limit: 1, SubaccountID: value });
                const found = res.Subaccounts.find((a) => a.AccountID === value);
                if (!found) {
                  setFromBankLabel(label);
                  applyBalance(undefined);
                  return;
                }
                const balance =
                  Object.values(found.Balances || {}).find((b) => b.Currency === ctx.values.Currency)
                    ?.AvailableBalance || '0.00';
                // Match the dropdown option label, which uses the list endpoint's
                // AccountName (OriginatorName ?? AccountName).
                setFromBankLabel(`${found.OriginatorName || found.AccountName} ($${vp.formattedCurrency(balance)})`);
                applyBalance(balance);
              } catch (error) {
                console.error(error);
                setFromBankLabel(label);
                applyBalance(undefined);
              }
            }}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('To.label'),
      fieldName: 'RecipientAccountID',
      getInputState: () => 'required',
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const ToBankAccountsOptions = buildSubAccountOptions(
          toSubAccounts.filter((data) => ctx.values.DebitorAccountID !== data.APIAccountID),
          ctx.values.DebitorAccountID === MainAccountId,
          toSearchText,
        );
        return (
          <Select
            version="2"
            options={ToBankAccountsOptions}
            placeholder={t('To.placeholder')}
            sort={false}
            filterable
            loading={toFetching}
            emptyFilterMsg={t('To.noResults', { defaultValue: 'No sub-accounts found' })}
            onFilter={(e) => {
              e.preventDefault();
              setToSearchText(e.filter);
              if (e.filter.length > 0 && e.filter.length < SEARCH_MIN_CHARS) return;
              e.debounce(() => onSearchToSubAccounts(e.filter), SEARCH_DEBOUNCE_MS);
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;
              return <>{toBankLabel || value}</>;
            }}
            {...fieldData}
            onChange={async (e, { value, label }) => {
              fieldData.onChange(e);
              if (!value) return;
              if (value === MainAccountId) {
                setToBankLabel(`${MainAccountName} ($${vp.formattedCurrency(MainAccountBalance || '0.00')})`);
                return;
              }
              try {
                const res = await fetchToSubAccountsAsync({ ActiveOnly: true, Limit: 1, SubaccountID: value });
                const found = res.Subaccounts.find((a) => a.AccountID === value);
                if (!found) {
                  setToBankLabel(label);
                  return;
                }
                const balance =
                  Object.values(found.Balances || {}).find((b) => b.Currency === ctx.values.Currency)
                    ?.AvailableBalance || '0.00';
                // Match the dropdown option label, which uses the list endpoint's
                // AccountName (OriginatorName ?? AccountName).
                setToBankLabel(`${found.OriginatorName || found.AccountName} ($${vp.formattedCurrency(balance)})`);
              } catch (error) {
                console.error(error);
                setToBankLabel(label);
              }
            }}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Amount.label'),
      fieldName: 'Amount',
      getInputState: () => 'required',
      serializeField: (value) => Number(vp.sanitizeToNumeric(value.Amount)),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField preset="currency" {...fieldData} variant="outlineShadow" />;
      },
      validate: (ctx) => {
        const availableBalance = ctx.values.FromBalance;

        return Yup.string()
          .test('amount-validation', t('Validation.minimumAmount'), (value) => {
            const amountValidation = Number(vp.sanitizeToNumeric(value)) <= 0.0;
            return !amountValidation;
          })
          .test(
            'amount-validation',
            t('Validation.insufficientFunds', {
              maxAmount: vp.formattedCurrency(String(availableBalance ?? '0.00')),
            }),
            (value) => {
              if (availableBalance === undefined) return true;
              const max = Number(vp.sanitizeToNumeric(availableBalance));
              if (Number.isNaN(max)) return true;
              return Number(vp.sanitizeToNumeric(value)) <= max;
            },
          )
          .required();
      },
      showField: () => true,
    },
  ];
  function buildSubAccountOptions(
    lists: vp.PortalSubAccount[],
    removeMain: boolean,
    searchText: string,
  ): { label: string; value: string }[] {
    // The list endpoint already returns accounts sorted by name, so no client
    // sort is needed (Select keeps `sort={false}`).
    // The parent account is prepended client-side, so apply the same search rule
    // to it: only show it when no active search or when its name matches.
    const isSearching = searchText.length >= SEARCH_MIN_CHARS;
    const mainMatches = MainAccountName.toLowerCase().includes(searchText.toLowerCase());
    const initialOption =
      removeMain || (isSearching && !mainMatches) ? [] : [{ label: MainAccountName, value: MainAccountId }];

    return [
      ...initialOption,
      ...lists.map((data) => ({
        value: data.APIAccountID,
        label: data.AccountName,
      })),
    ];
  }
};
